import { jwtDecode } from 'jwt-decode'
import merge from 'lodash/merge'
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState
} from 'react'
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs'
import { routes } from '../App'
import api from '../api'
import { client_id as demoClientId, initDemo } from '../api/demo'
import { broadcastChannel, joinChannel, leaveChannel } from '../broadcast'

const loadingObservable = new Observable((subscriber) => {
  loadingObservable.increase = () => {
    subscriber.next(++loadingObservable.counter)
  }
  loadingObservable.decrease = () => {
    subscriber.next(--loadingObservable.counter)
  }
})
loadingObservable.counter = 0
loadingObservable.increase = () => {
  loadingObservable.counter++
}
loadingObservable.decrease = () => {
  loadingObservable.counter--
}

const initialUserState = {
  datasources: [],
  session: null,
  settings: null,
  theme: null,
  view_as_role: null
}

const findBaseRoute = (path, r) => {
  const basePath = path
    .split('/')
    .filter((p) => !r.test(p))
    .slice(0, 2)
    .join('/')

  return (
    routes.find((r) => r.path === `${basePath}/archive`) ||
    routes.find((r) => r.path === basePath)
  )?.path
}

const UserContext = createContext()

export const decrLoading = () => {
  loadingObservable.decrease()
}
export const incrLoading = () => {
  loadingObservable.increase()
}

export let getUserClient = () => {}
export let getUserDatasources = () => {}
export let getUserSettings = () => initialUserState
export let updateUser = (updates) => {
  merge(initialUserState, updates)
}

export const handleLogout$ = () =>
  api.authentication.logout$().pipe(
    map((res) => {
      leaveChannel()
      updateUser(initialUserState)
      return res
    })
  )

export const restart = (broadcast = true) => {
  const r = /\/(edit|test)\/\d+/
  const path = window.location.pathname

  if (!r.test(path)) {
    broadcast && broadcastChannel({ type: 'RESTART' })
    return window.location.reload()
  }

  const parts = path.split('/').filter(Boolean)
  let baseRoute = findBaseRoute(path, r)

  while (!baseRoute && parts.length > 0) {
    parts.pop()
    baseRoute = findBaseRoute(`/${parts.join('/')}`, r)
  }

  broadcast && broadcastChannel({ type: 'RESTART' })
  window.location.assign(baseRoute || '/dashboard')
}

export function UserProvider({ children }) {
  const [loading, setLoading] = useState(0)
  const [openSidebar, setOpenSidebar] = useState(false)
  const [user, setUser] = useState(initialUserState)

  const toggleSidebar = useCallback(
    (fn = (prevState) => !prevState) => setOpenSidebar(fn),
    []
  )

  const getCustomSettings$ = useCallback(
    (key, id = user.session.sub.id, extraOpts) =>
      api.usersettings
        .find$({ limit: null, skip: null, ...extraOpts }, { id })
        .pipe(
          switchMap(({ data: [{ custom }] }) => {
            custom = !custom || Array.isArray(custom) ? {} : custom
            return of(key ? custom[key] : custom)
          })
        ),
    [user.session?.sub.id]
  )

  const handleSession$ = useCallback((token, session) => {
    if (token) {
      localStorage.setItem('loginToken', token)
      joinChannel()
      session = jwtDecode(token)
    }

    return forkJoin([
      of(session),
      api.usersettings.find$(undefined, { id: session.sub.id }),
      api.datasources.findAll$(),
      api.clients.findAll$()
    ]).pipe(
      switchMap(
        ([
          session,
          {
            data: [settings]
          },
          { data: datasources },
          { data: clients }
        ]) => {
          const client = clients.find((c) => c.client_id === settings.client_id)
          const nextUser = { client, clients, datasources, session, settings }

          setUser((prevState) => ({ ...prevState, ...nextUser }))
          const joins = [of(nextUser)]

          if (!session.sub.superadmin && settings.client_id === demoClientId) {
            window.isDemo = true
            joins.push(from(initDemo()))
          }

          return forkJoin(joins)
        }
      )
    )
  }, [])

  const hasAdmin = useCallback(
    () =>
      (user.session?.sub.superadmin && !user.view_as_role) ||
      user.session?.sub.administrator ||
      user.settings.roles.includes('administrator'),
    [
      user.session?.sub.administrator,
      user.session?.sub.superadmin,
      user.settings?.roles,
      user.view_as_role
    ]
  )

  const hasSuperadmin = useCallback(
    () => user.session?.sub.superadmin && !user.view_as_role,
    [user.session?.sub.superadmin, user.view_as_role]
  )

  const hasPermissions = useCallback(
    (perms, needsAllPerms = true) => {
      if (!perms || !user.settings?.permissions || hasSuperadmin()) {
        return true
      }

      if (typeof perms === 'string') {
        perms = [perms]
      }

      const userPerms = Object.entries(
        user.view_as_role || user.settings?.permissions || {}
      ).flatMap(([endpoint, verbs]) =>
        verbs.map((verb) => `${endpoint}:${verb}`)
      )

      return needsAllPerms
        ? perms.every((p) => userPerms.includes(p))
        : perms.some((p) => userPerms.includes(p))
    },
    [hasSuperadmin, user.settings?.permissions, user.view_as_role]
  )

  const saveCustomSettings$ = useCallback(
    (newSettings = {}, id = user.session.sub.id, extraOpts) =>
      getCustomSettings$(undefined, id, extraOpts).pipe(
        switchMap((custom) => {
          const nextState = {
            ...custom,
            ...newSettings
          }

          setUser((prevState) => ({
            ...prevState,
            settings: {
              ...prevState.settings,
              custom: nextState
            }
          }))

          return api.usersettings.create$(
            { custom: nextState },
            { id },
            extraOpts
          )
        })
      ),
    [getCustomSettings$, user.session?.sub.id]
  )

  const saveSettings$ = useCallback(
    (newSettings, id = user.session?.sub.id, extraOpts) =>
      user.session
        ? api.usersettings.create$(newSettings, { id }, extraOpts).pipe(
            switchMap(() =>
              // create$() does not return any permissions, so we need a subsequent
              // find$()
              api.usersettings.find$({ limit: null, skip: null }, { id })
            ),
            map(({ data: [settings] }) => {
              setUser((prevState) => ({
                ...prevState,
                settings
              }))
              return settings
            })
          )
        : of(newSettings),
    [user.session]
  )

  const setClient = useCallback((client) =>
    setUser((prevState) => ({
      ...prevState,
      client
    }))
  )

  const setViewAsRole = useCallback(
    (role) =>
      setUser((prevState) => ({
        ...prevState,
        view_as_role: role
      })),
    []
  )

  useLayoutEffect(() => {
    updateUser = (updates = {}) =>
      setUser((prevState) => ({
        ...prevState,
        ...updates
      }))

    return () => {
      updateUser = () => {}
    }
  }, [])

  useLayoutEffect(() => {
    const sub = loadingObservable.subscribe(setLoading)

    return () => {
      sub.unsubscribe()
    }
  }, [])

  useLayoutEffect(() => {
    getUserClient = () => user.client
    getUserDatasources = () => user.datasources
    getUserSettings = () => user.settings

    return () => {
      getUserClient = () => {}
      getUserDatasources = () => {}
      getUserSettings = () => {}
    }
  }, [user])

  return (
    <UserContext.Provider
      value={{
        getCustomSettings$,
        handleSession$,
        hasAdmin,
        hasPermissions,
        hasSuperadmin,
        loading,
        openSidebar,
        saveCustomSettings$,
        saveSettings$,
        setClient,
        setViewAsRole,
        toggleSidebar,
        user
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default function useModals() {
  return useContext(UserContext)
}
